/**
 * TestingPathSection — one live path and its landers/offers in the grouped
 * Testing view. Collapsible via its header chevron; the open state is owned by
 * TestingGroupedView so a single "Collapse all" can drive every section.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type {
  DemoteDestination,
  QueueElement,
  QueuePoolSummary,
  RotationElementDiff,
} from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import type { BulkRowProps } from '../../bulk/row/bulk-row.types';
import { poolElementKey, type RotationSwap } from '../../rotation/rotation-swaps';
import { findPool } from '../QueueCapCell';
import { TestingGroupHeader, TestingItemRow } from './rows';
import { type TestingGroup } from './group-testing';

export interface RowMeta {
  campaignId: string;
  provider: QueueProvider;
  /** Move a live PATH off the tracker (paths only — header-level action). */
  onDemote: (element: QueueElement, to: DemoteDestination) => void;
  demotePending: boolean;
  /** Absent ⇒ the view renders without checkboxes. */
  bulk?: BulkRowProps;
}

export interface SectionCtx {
  pools: QueuePoolSummary[];
  atRisk: Map<string, RotationSwap>;
  blocked: Map<string, RotationElementDiff>;
  meta: RowMeta;
}

const verdicts = (el: QueueElement, ctx: SectionCtx) => ({
  swap: ctx.atRisk.get(poolElementKey(el)),
  held: ctx.blocked.get(poolElementKey(el)),
});

export const TestingPathSection: FC<{
  group: TestingGroup;
  ctx: SectionCtx;
  open: boolean;
  onToggle: () => void;
}> = ({ group, ctx, open, onToggle }) => {
  const { path, items } = group;
  // The path counts as one of its own section's changes: when the PATH leaves,
  // the section is the thing being rotated, and a header reading "0 leaving"
  // beside its own "Back to queue" verdict was the most confusing thing here.
  const leaving = [...(path ? [path] : []), ...items].filter((el) =>
    ctx.atRisk.has(poolElementKey(el)),
  ).length;
  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-card/40">
      {path ? (
        <TestingGroupHeader
          path={path}
          // Per-group pools: the cap meter must read THIS path's pool.
          pool={findPool(ctx.pools, 'paths', path.poolId)}
          itemCount={items.length}
          changeCount={leaving}
          open={open}
          onToggle={onToggle}
          campaignId={ctx.meta.campaignId}
          provider={ctx.meta.provider}
          onDemote={ctx.meta.onDemote}
          demotePending={ctx.meta.demotePending}
          bulk={ctx.meta.bulk}
          {...verdicts(path, ctx)}
        />
      ) : (
        <p className="bg-muted/30 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Other testing elements
        </p>
      )}
      {open
        ? items.map((el) => (
            <TestingItemRow
              key={`${el.layer}:${el.poolId}:${el.bankId}`}
              el={el}
              campaignId={ctx.meta.campaignId}
              provider={ctx.meta.provider}
              {...verdicts(el, ctx)}
            />
          ))
        : null}
    </div>
  );
};

export default TestingPathSection;
